package fyi.moko.hotpl.data.image

import fyi.moko.hotpl.data.network.PhotoDownloadException
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.PhotoBlobStore
import fyi.moko.hotpl.domain.service.PhotoSyncQueue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * A [PlaceImageStore] that also knows the difference between "on this device" and
 * "somewhere else". [load] hides it (local, else fetched); UI that wants to draw the two
 * states differently — the detail header must not put a photo layout over an illustration
 * while a download is in flight — uses the two halves directly.
 */
interface PhotoSyncStore : PlaceImageStore {
    /** The local file only. Never touches the network. */
    suspend fun loadLocal(fileName: String): ByteArray?

    /**
     * Fetches [fileName] from the current user's blob store and caches it locally. Null when
     * nobody is signed in, the object doesn't exist, or the download failed — the caller
     * can't tell those apart and shouldn't need to: all three draw as "no photo here yet".
     */
    suspend fun fetchRemote(fileName: String): ByteArray?

    /**
     * Records that this device threw away [fileName]'s upload because the bytes were already
     * gone when the queue got to it. The one thing a screen can then say that "not available
     * yet" cannot: waiting will not bring this photo back.
     */
    suspend fun markLost(fileName: String)

    /** Whether [markLost] was recorded for [fileName] on this device. */
    suspend fun isLost(fileName: String): Boolean
}

/**
 * The device-local [local] store wrapped so that every save queues an upload, every delete
 * queues a remote delete, and a read that misses locally is served from [blobs].
 *
 * ── 왜 저장소를 감싸는가 (호출부를 고치지 않고) ──────────────────────────
 * 사진을 저장하는 곳이 넷이다(장소 폼, 방문 기록, 카메라, 사진 정정). 각각에 "업로드도
 * 걸어라"를 붙이면 다섯 번째 호출부가 생길 때 빠뜨린다. 저장소 하나가 규칙을 들고 있으면
 * 호출부는 계속 "로컬에 저장한다"만 알면 된다.
 *
 * The cache write on fetch reuses the remote name unchanged, so the file that lands here is
 * exactly the one the Firestore document already points at.
 */
class CloudBackedPlaceImageStore(
    private val local: PlaceImageStore,
    private val blobs: PhotoBlobStore,
    private val queue: PhotoSyncQueue,
    private val auth: AuthService,
) : PhotoSyncStore {

    /** Serialises fetches so two views asking for the same missing file download it once. */
    private val fetchLock = Mutex()

    override suspend fun save(bytes: ByteArray): String {
        val name = local.save(bytes)
        queue.enqueueUpload(name)
        return name
    }

    override suspend fun saveAs(fileName: String, bytes: ByteArray) = local.saveAs(fileName, bytes)

    override suspend fun load(fileName: String): ByteArray? = loadLocal(fileName) ?: fetchRemote(fileName)

    override suspend fun loadLocal(fileName: String): ByteArray? = local.load(fileName)

    /**
     * ── 드롭 표식은 이미지 저장소 안의 파일이다 (0-18 A-2) ──────────────────
     * Room 컬럼도, Firestore 필드도 아니다. 스키마를 안 건드리고, 복원(`clear()`)이 파일을
     * 같이 지워 주며, 백필 마커와 같은 자리에 산다. 표식은 이 기기의 사실("여기서 사라졌다")
     * 이지 계정의 사실이 아니라서 다른 기기로 동기화하지 않는다 — 다른 기기가 나중에 올려
     * 주면 [fetchRemote]가 성공하며 표식을 걷어낸다.
     */
    override suspend fun markLost(fileName: String) = local.saveAs(lostMarker(fileName), byteArrayOf(1))

    override suspend fun isLost(fileName: String): Boolean = local.load(lostMarker(fileName)) != null

    override suspend fun fetchRemote(fileName: String): ByteArray? {
        val uid = auth.getCurrentUserId() ?: return null
        return fetchLock.withLock {
            // Another caller may have fetched it while we waited for the lock.
            local.load(fileName)?.let { return@withLock it }
            val bytes = try {
                blobs.download(uid, fileName)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // Class name (plus HTTP status for our own failure type) only — a transport
                // exception's message can carry the tokenised download URL.
                val detail = (e as? PhotoDownloadException)?.let { " status=${it.status}" } ?: ""
                println("CloudBackedPlaceImageStore: fetch failed for a photo: ${e::class.simpleName}$detail")
                null
            } ?: return@withLock null
            // Caching is best-effort: a full disk (ImageStoreFullException on iOS/web) must
            // not turn a successful download into "no photo". The bytes still draw; the
            // next open simply downloads again.
            try {
                local.saveAs(fileName, bytes)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("CloudBackedPlaceImageStore: could not cache a fetched photo: ${e::class.simpleName}")
            }
            // A copy did exist after all (another device's backfill, most likely).
            if (local.load(lostMarker(fileName)) != null) local.delete(lostMarker(fileName))
            bytes
        }
    }

    override suspend fun delete(fileName: String) {
        local.delete(fileName)
        queue.enqueueDelete(fileName)
    }

    /** Local only: a restore wipes this device's files, not the account's photos. */
    override suspend fun clear() = local.clear()

    private fun lostMarker(fileName: String) = "$LOST_MARKER_PREFIX$fileName"

    companion object {
        internal const val LOST_MARKER_PREFIX = ".lost-"
    }
}
